import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle, Clock, AlertTriangle, ArrowRight, User,
  Package, FileText, DollarSign, Briefcase, MapPin,
  Calendar, MessageSquare, Wrench, XCircle, Target,
  ShieldAlert, ListChecks, Home, ExternalLink, Zap,
  Truck, ClipboardCheck, Archive, Users, Navigation,
} from "lucide-react";
import type { Job, Issue } from "@/data/types";
import type { Quote } from "@/data/quoteTypes";
import type { Contractor } from "@/data/types";
import {
  getJobIssueDetail,
  getIssueEntityDetail,
  getQuoteIssueDetail,
  getContractorIssueDetail,
  type StructuredIssueDetail,
  type ActionLink,
} from "@/data/issueIntelligence";
import { formatStandardDate, formatStandardTimestamp } from "@/lib/dateFormat";
import { formatAddress } from "@/lib/addressUtils";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";

/* ─── Types ─── */
export type AttentionItemStatus = "active" | "in_progress" | "completed";

export interface AttentionItem {
  id: string;
  title: string;
  entity: "Job" | "Issue" | "Quote" | "Contractor";
  reason: string;
  severity: "critical" | "warning";
  link: string;
  age?: string;
  jobId?: string;
  issueId?: string;
  quoteId?: string;
  contractorId?: string;
  details?: string[];
  whyItMatters?: string;
  primaryAction?: string;
  secondaryAction?: string;
  secondaryLink?: string;
}

export interface AttentionItemAction {
  itemId: string;
  status: AttentionItemStatus;
  notes?: string;
  acknowledgedBy: string;
  timestamp: string;
}

const STATUS_CONFIG: Record<AttentionItemStatus, { label: string; color: string; icon: React.ElementType }> = {
  active: { label: "Active", color: "bg-destructive/10 text-destructive border-destructive/20", icon: AlertTriangle },
  in_progress: { label: "In Progress", color: "bg-warning/10 text-warning border-warning/20", icon: Clock },
  completed: { label: "Completed", color: "bg-success/10 text-success border-success/20", icon: CheckCircle },
};

const ACTION_ICONS: Record<ActionLink["icon"], React.ElementType> = {
  dispatch: Truck,
  job: Briefcase,
  materials: Package,
  permit: FileText,
  schedule: Calendar,
  qa: ClipboardCheck,
  inventory: Archive,
  issues: AlertTriangle,
  contractor: Users,
};

interface Props {
  item: AttentionItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentStatus: AttentionItemStatus;
  actionHistory: AttentionItemAction[];
  jobs: Job[];
  issues: Issue[];
  quotes: Quote[];
  contractors: Contractor[];
  onAction: (itemId: string, status: AttentionItemStatus, notes?: string) => void;
}

export function AttentionItemDetailModal({
  item, open, onOpenChange, currentStatus, actionHistory, jobs, issues, quotes, contractors, onAction,
}: Props) {
  const [responseNotes, setResponseNotes] = useState("");

  if (!item) return null;

  const job = item.jobId ? jobs.find((j) => j.id === item.jobId) : undefined;
  const issue = item.issueId ? issues.find((i) => i.id === item.issueId) : undefined;
  const quote = item.quoteId ? quotes.find((q) => q.id === item.quoteId) : undefined;
  const contractor = item.contractorId ? contractors.find((c) => c.id === item.contractorId) : undefined;

  const statusCfg = STATUS_CONFIG[currentStatus];
  const StatusIcon = statusCfg.icon;
  const itemActions = actionHistory.filter((a) => a.itemId === item.id);

  const openIssues = job ? issues.filter((i) => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress")) : [];
  let detail: StructuredIssueDetail;

  if (item.entity === "Issue" && issue) {
    detail = getIssueEntityDetail(issue, job);
  } else if (item.entity === "Quote" && quote) {
    detail = getQuoteIssueDetail(quote, item.reason);
  } else if (item.entity === "Contractor" && contractor) {
    const cIssues = issues.filter((i) => {
      const j = jobs.find((jb) => jb.id === i.jobId);
      return j && (j.assignedSubcontractor === contractor.id || j.assignedCrew === contractor.companyName) && (i.status === "Open" || i.status === "In Progress");
    });
    detail = getContractorIssueDetail(contractor, cIssues.length, 0);
  } else if (job) {
    detail = getJobIssueDetail(job, item.reason, openIssues);
  } else {
    detail = {
      primaryTitle: item.title,
      secondaryLine: "",
      streetAddress: "",
      city: "Unknown",
      issue: item.reason,
      stage: "Unknown",
      blocker: "Awaiting field input — assign owner to investigate and define blocker",
      impact: "Operational risk — review and resolve to maintain project momentum",
      homeownerConcern: "No direct homeowner concern reported at this time",
      actionRequired: "Open the full record to investigate and determine next steps.",
      actionLinks: [{ label: "Open Record", path: item.link, icon: "job" }],
      validatedSeverity: "warning",
    };
  }

  const handleComplete = () => {
    onAction(item.id, "completed", responseNotes || undefined);
    setResponseNotes("");
    onOpenChange(false);
    toast.success("Issue marked as completed");
  };

  const handleInProgress = () => {
    if (!responseNotes.trim()) return;
    onAction(item.id, "in_progress", responseNotes);
    setResponseNotes("");
    onOpenChange(false);
    toast.success("Status updated — issue remains active");
  };

  const createdDate = issue?.dateCreated || (job as any)?.dateCreated || "";
  const effectiveSeverity = detail.validatedSeverity;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className={`text-[9px] font-bold uppercase ${statusCfg.color}`}>
              <StatusIcon className="h-3 w-3 mr-1" />{statusCfg.label}
            </Badge>
            <Badge variant="outline" className={`text-[9px] font-bold uppercase ${
              effectiveSeverity === "critical" ? "bg-destructive/10 text-destructive border-destructive/20" : "bg-warning/10 text-warning border-warning/20"
            }`}>{effectiveSeverity}</Badge>
            {createdDate && (
              <span className="text-[9px] text-muted-foreground ml-auto">{formatStandardDate(createdDate)}</span>
            )}
          </div>
          <DialogTitle className="text-base leading-snug">{detail.primaryTitle}</DialogTitle>
          {detail.secondaryLine && (
            <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{detail.secondaryLine}</p>
          )}
        </DialogHeader>

        <div className="space-y-3 mt-2">

          {/* ═══ Structured Issue Detail ═══ */}
          <div className="rounded-lg border bg-muted/20 overflow-hidden">
            {/* Issue */}
            <div className="p-3 border-b border-border/50">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="h-5 w-5 rounded bg-destructive/10 flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-3 w-3 text-destructive" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-destructive">Issue</span>
              </div>
              <p className="text-xs leading-relaxed">{detail.issue}</p>
            </div>

            {/* Stage */}
            <div className="p-3 border-b border-border/50">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="h-5 w-5 rounded bg-primary/10 flex items-center justify-center shrink-0">
                  <Target className="h-3 w-3 text-primary" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary">Stage</span>
              </div>
              <p className="text-xs font-medium">{detail.stage}</p>
            </div>

            {/* Blocker */}
            <div className="p-3 border-b border-border/50">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="h-5 w-5 rounded bg-warning/10 flex items-center justify-center shrink-0">
                  <ShieldAlert className="h-3 w-3 text-warning" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-warning">Blocker</span>
              </div>
              <p className="text-xs leading-relaxed">{detail.blocker}</p>
            </div>

            {/* Impact */}
            <div className="p-3 border-b border-border/50">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="h-5 w-5 rounded bg-destructive/10 flex items-center justify-center shrink-0">
                  <Zap className="h-3 w-3 text-destructive" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-destructive">Impact</span>
              </div>
              <p className="text-xs leading-relaxed">{detail.impact}</p>
            </div>

            {/* Homeowner Concern */}
            <div className="p-3 border-b border-border/50">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="h-5 w-5 rounded bg-accent/30 flex items-center justify-center shrink-0">
                  <Home className="h-3 w-3 text-accent-foreground" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-accent-foreground">Homeowner Concern</span>
              </div>
              <p className="text-xs leading-relaxed">{detail.homeownerConcern}</p>
            </div>

            {/* Action Required */}
            <div className="p-3 bg-primary/[0.03]">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="h-5 w-5 rounded bg-primary/10 flex items-center justify-center shrink-0">
                  <ListChecks className="h-3 w-3 text-primary" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary">Action Required</span>
              </div>
              <p className="text-xs leading-relaxed font-medium">{detail.actionRequired}</p>
            </div>
          </div>

          {/* ═══ Action Buttons ═══ */}
          {detail.actionLinks.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Quick Actions</p>
              <div className="grid grid-cols-2 gap-1.5">
                {detail.actionLinks.slice(0, 4).map((al, i) => {
                  const Icon = ACTION_ICONS[al.icon] || Briefcase;
                  const isPrimary = i === 0;
                  return (
                    <Link
                      key={al.path + al.label}
                      to={al.path}
                      onClick={() => onOpenChange(false)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs font-medium transition-colors ${
                        isPrimary
                          ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                          : "bg-muted/50 hover:bg-muted border-border"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      {al.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* ═══ Entity-Specific Context ═══ */}
          {job && (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold">
                <Briefcase className="h-3.5 w-3.5 text-primary" /> Job Context
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                <div className="flex items-center gap-1.5">
                  <User className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Customer:</span>
                  <span className="font-medium">{job.customerName}</span>
                </div>
                {job.address && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                    <span className="text-muted-foreground truncate">{formatAddress(job.address)}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Stage:</span>
                  <span className="font-medium">{job.currentStage}</span>
                </div>
                {job.scheduledInstallDate && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3 w-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Install:</span>
                    <span className="font-medium">{formatStandardDate(job.scheduledInstallDate)}</span>
                  </div>
                )}
                {job.financials && (
                  <div className="flex items-center gap-1.5">
                    <DollarSign className="h-3 w-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Value:</span>
                    <span className="font-medium">${getJobDisplayValue(job).toLocaleString()}</span>
                  </div>
                )}
                {job.financials?.paymentStatus && (
                  <div className="flex items-center gap-1.5">
                    <DollarSign className="h-3 w-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Payment:</span>
                    <Badge variant="outline" className="text-[9px] h-4">{job.financials.paymentStatus}</Badge>
                  </div>
                )}
              </div>

              {/* Missing Materials Detail */}
              {(job.materialReadiness === "Missing Items") && job.materialsList && (
                <div className="mt-2 pt-2 border-t border-border/50">
                  <div className="flex items-center gap-1.5 text-xs font-semibold mb-1.5">
                    <Package className="h-3.5 w-3.5 text-destructive" /> Missing Materials
                  </div>
                  <div className="space-y-1">
                    {job.materialsList.filter((m) => m.missingItems > 0).map((m, i) => (
                      <div key={i} className="flex items-center justify-between text-[11px] py-1 px-2 rounded bg-destructive/5 border border-destructive/10">
                        <span className="font-medium">{m.itemName}</span>
                        <div className="flex items-center gap-2 text-[10px]">
                          <span className="text-muted-foreground">Need: {m.quantityRequired} / Have: {m.quantityAllocated}</span>
                          <Badge variant="outline" className="text-[9px] h-4 border-destructive/30 text-destructive">{m.inventoryStatus}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Permit Detail */}
              {job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required" && job.permitDetail && (
                <div className="mt-2 pt-2 border-t border-border/50">
                  <div className="flex items-center gap-1.5 text-xs font-semibold mb-1.5">
                    <FileText className="h-3.5 w-3.5 text-warning" /> Permit Detail
                  </div>
                  <div className="space-y-1 text-[11px]">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Municipality</span>
                      <span className="font-medium">{job.permitDetail.municipality}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Stage</span>
                      <Badge variant="outline" className="text-[9px] h-4 border-warning/30 text-warning">{job.permitDetail.detailedStage}</Badge>
                    </div>
                    {job.permitDetail.blockers && (
                      <div className="mt-1 p-2 rounded bg-destructive/5 border border-destructive/10">
                        <p className="text-[10px] text-muted-foreground">{job.permitDetail.blockers}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Issue entity detail */}
          {issue && !job && (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold">
                <AlertTriangle className="h-3.5 w-3.5 text-destructive" /> Issue Record
              </div>
              <div className="space-y-1.5 text-[11px]">
                <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span className="font-medium">{issue.issueType}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Severity</span><Badge variant="outline" className={`text-[9px] h-4 ${issue.severity === "Urgent" ? "text-destructive border-destructive/30" : "text-warning border-warning/30"}`}>{issue.severity}</Badge></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Owner</span><span className="font-medium">{issue.owner}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Source</span><span className="font-medium">{issue.source}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span className="font-medium">{formatStandardDate(issue.dateCreated)}</span></div>
                {issue.affectedArea && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Affected Area</span><span className="font-medium">{issue.affectedArea}</span></div>
                )}
              </div>
            </div>
          )}

          {/* Quote detail */}
          {quote && (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5 text-[11px]">
              <div className="flex items-center gap-2 text-xs font-semibold mb-1">
                <FileText className="h-3.5 w-3.5 text-primary" /> Quote Context
              </div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge variant="outline" className="text-[9px] h-4">{quote.status}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Updated</span><span className="font-medium">{formatStandardDate(quote.updatedAt)}</span></div>
              {quote.totalEstimate > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Value</span><span className="font-medium">${quote.totalEstimate.toLocaleString()}</span></div>}
            </div>
          )}

          {/* Contractor detail */}
          {contractor && (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5 text-[11px]">
              <div className="flex items-center gap-2 text-xs font-semibold mb-1">
                <Wrench className="h-3.5 w-3.5 text-muted-foreground" /> Contractor Context
              </div>
              <div className="flex justify-between"><span className="text-muted-foreground">Company</span><span className="font-medium">{contractor.companyName}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Contact</span><span className="font-medium">{contractor.contactName}</span></div>
            </div>
          )}

          {/* ═══ Activity Ledger ═══ */}
          <div className="rounded-lg border p-3">
            <div className="flex items-center gap-2 text-xs font-semibold mb-2">
              <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" /> Issue Activity Ledger
            </div>
            {itemActions.length > 0 ? (
              <div className="space-y-2">
                {itemActions.map((action, i) => {
                  const cfg = STATUS_CONFIG[action.status];
                  const ActionIcon = cfg.icon;
                  return (
                    <div key={i} className="flex items-start gap-2 text-[11px]">
                      <ActionIcon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${
                        action.status === "completed" ? "text-success" : action.status === "in_progress" ? "text-warning" : "text-muted-foreground"
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{action.acknowledgedBy}</span>
                          <span className="text-muted-foreground">→ {cfg.label}</span>
                          <span className="text-muted-foreground/60 ml-auto shrink-0">{formatStandardTimestamp(action.timestamp)}</span>
                        </div>
                        {action.notes && <p className="text-muted-foreground mt-0.5 leading-snug">{action.notes}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground italic">No activity recorded yet</p>
            )}
          </div>

          {/* ═══ Actions ═══ */}
          {currentStatus !== "completed" && (
            <div className="space-y-3 pt-2 border-t">
              <p className="text-xs font-semibold">Update Status</p>

              <Button
                className="w-full justify-start gap-2"
                variant="outline"
                onClick={handleComplete}
              >
                <CheckCircle className="h-4 w-4 text-success" /> Mark as Completed
              </Button>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-warning" />
                  <p className="text-xs font-medium">Not completed? Explain current status:</p>
                </div>
                <Textarea
                  value={responseNotes}
                  onChange={(e) => setResponseNotes(e.target.value)}
                  placeholder="What is being done to resolve this issue..."
                  rows={3}
                  className="text-xs"
                />
                <Button
                  className="w-full gap-2"
                  variant="secondary"
                  size="sm"
                  disabled={!responseNotes.trim()}
                  onClick={handleInProgress}
                >
                  <Clock className="h-3.5 w-3.5" /> Mark In Progress & Save Notes
                </Button>
              </div>
            </div>
          )}

          {currentStatus === "completed" && (
            <div className="text-center py-3 rounded-lg bg-success/5 border border-success/20">
              <CheckCircle className="h-6 w-6 text-success mx-auto mb-1" />
              <p className="text-sm font-medium text-success">Issue Resolved</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">This item has been marked as completed</p>
            </div>
          )}

          {/* Navigate Link */}
          <Link
            to={item.link}
            onClick={() => onOpenChange(false)}
            className="flex items-center justify-center gap-2 text-xs text-primary hover:underline pt-1"
          >
            Open Full Record <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
